import { useEffect, useRef, useState } from "react";
import { Dialog, Typography, Stack, CircularProgress, Box, useTheme, Grid2 as Grid, Card, Button, Divider, Tooltip, IconButton, Paper, Chip, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Cancel01Icon, Notification01Icon, VolumeLowIcon, VolumeMute02Icon, WhatsappIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import DataCell from "@/components/DataCell";
import Price from "@/components/Price";
import { MENU_FLOW_LABELS, MENU_FLOWS } from "@/constants/global";
import { Order, OrderItem } from "@/types";
import { getOrderDetails, getOrderTotal } from "@/utils/order";
import alertSound from "@/assets/alerts/IncomingOrder.mp3";
import { useAppSelector, useUserProfile } from "@/hooks";
import { formatPhone } from "@/utils/phones";
// import useNeighborhood from "@/hooks/useNeighborhood";
import useServiceFee from "@/hooks/useServiceFee";
import { purple } from "@mui/material/colors";

interface Props {
  open: boolean;
  onClose?: () => void;
  order: Order | null;
  onAcceptOrder?: () => void;
  onRejectOrder?: (reason: string) => void;
}

const alertAudio = typeof window !== "undefined" ? new Audio(alertSound) : null;
if (alertAudio) {
  alertAudio.loop = true;
}

const IncomingOrderDialog = ({ open, onClose, order, onAcceptOrder, onRejectOrder }: Props) => {
  const { palette } = useTheme();
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const organization = useAppSelector((state) => state.organizations[organizationId]);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const { activeUser } = useUserProfile();
  const isAdmin = activeUser?.role === "super-admin" || ["admin", "owner"].includes(activeUser?.access?.[organizationId]?.role ?? "");
  // const { getNeighborhoodName } = useNeighborhood();
  const service = useServiceFee();
  const [progressValue, setProgressValue] = useState(100);
  const [isRejecting, setIsRejecting] = useState(false);

  const formik = useFormik({
    initialValues: {
      rejectedReason: "",
    },
    validationSchema: Yup.object({
      rejectedReason: Yup.string()
        .required("Campo requerido")
        .min(10, "El motivo debe tener al menos 10 caracteres")
        .max(250, "El motivo no puede exceder los 250 caracteres"),
    }),
    onSubmit: (values) => {
      if (onRejectOrder) {
        onRejectOrder(values.rejectedReason);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      setIsRejecting(false);
      formik.resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (!alertAudio) return;

    if (open) {
      // Start playing the sound when the dialog opens
      alertAudio.play().then(() => {
        setIsAudioMuted(false);
      }).catch((error) => {
        // Handle potential errors (e.g., browser restrictions on auto-play)
        console.error("Audio playback failed:", error);
        if (error instanceof DOMException && error.name === "NotAllowedError") {
          setIsAudioMuted(true);
        }
      });
    } else {
      // Stop the sound when the dialog closes
      alertAudio.pause();
      // Reset playback to the start for the next time it plays
      alertAudio.currentTime = 0;
    }

    // The cleanup function runs when the component unmounts or before the effect runs again
    return () => {
      // Ensure the sound is stopped if the component is removed from the DOM
      alertAudio.pause();
      alertAudio.currentTime = 0;
    };
  }, [open]); // Re-run this effect whenever 'open' or the source changes

  useEffect(() => {
    if (!open || (!order?.orderedAt && !order?.expiresAt)) {
      setProgressValue(100);
      return;
    }

    const durationMs = 15 * 60 * 1000;
    const expirationTime = order.expiresAt || (order.orderedAt! + durationMs);

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, expirationTime - now);
      const totalWindow = (order.expiresAt && order.orderedAt) ? (order.expiresAt - order.orderedAt) : durationMs;

      setProgressValue((remaining / totalWindow) * 100);
    };

    updateProgress();
    const intervalId = setInterval(updateProgress, 1000);

    return () => clearInterval(intervalId);
  }, [open, order?.orderedAt, order?.expiresAt]);


  // TODO: Fix Branch in order so we only ask for branchId
  const shouldShowDialog = open && (!activeBranch || order?.branchId === activeBranch || order?.branch?.id === activeBranch);

  return (
    <Dialog open={shouldShowDialog} onClose={onClose} disableEscapeKeyDown={true}>
      <Stack spacing={2} sx={{ padding: { xs: 2, lg: 4 }, minWidth: { sm: 400, lg: 500 } }}>
        {isRejecting ? (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              <Typography variant="h3" align="center">Rechazar Pedido</Typography>
              <TextField
                autoFocus
                multiline
                rows={3}
                id="rejectedReason"
                name="rejectedReason"
                label="Motivo del rechazo"
                value={formik.values.rejectedReason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.rejectedReason && Boolean(formik.errors.rejectedReason)}
                helperText={formik.touched.rejectedReason && formik.errors.rejectedReason}
                fullWidth
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => setIsRejecting(false)} fullWidth color="inherit" variant="text">
                  Cancelar
                </Button>
                <Button type="submit" fullWidth color="error" variant="contained" sx={{ borderRadius: 1, background: "linear-gradient(180deg, #EF7674 0%, #C63E3C 100%)" }}>
                  Confirmar
                </Button>
              </Stack>
            </Stack>
          </form>
        ) : (
          <>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <IconButton
                size="large"
                component={Paper}
                elevation={2}
                sx={{ backgroundColor: "#ffffff" }}
                onClick={() => {
                  if (isAudioMuted) {
                    alertAudio?.play();
                    setIsAudioMuted(false);
                  } else {
                    alertAudio?.pause();
                    setIsAudioMuted(true);
                  }
                }}
              >
                <HugeiconsIcon color={isAudioMuted ? palette.error.main : palette.primary.main} icon={isAudioMuted ? VolumeMute02Icon : VolumeLowIcon} />
              </IconButton>

              {isAdmin ? (
                <IconButton
                  size="large"
                  component={Paper}
                  elevation={2}
                  sx={{ backgroundColor: "#ffffff" }}
                  onClick={() => {
                    if (window.confirm("¿Deseas cerrar este diálogo? La alarma para este pedido se silenciará.")) {
                      if (alertAudio) {
                        alertAudio.pause();
                        setIsAudioMuted(true);
                      }
                      if (onClose) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onClose({}, "closeButtonClick");
                      }
                    }
                  }}
                >
                  <HugeiconsIcon icon={Cancel01Icon} color={palette.error.main} />
                </IconButton>
              ) : null}
            </Stack>
            <Stack spacing={3} padding={2} alignItems={"center"} justifyContent={"center"}>
              <Box
                sx={{
                  bgcolor: "#DDEAFD",
                  height: 60,
                  width: 60,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={progressValue}
                    size={84}
                    thickness={2}
                    color={progressValue <= 25 ? "error" : progressValue <= 50 ? "warning" : "primary"}
                  />
                </Box>
                <HugeiconsIcon
                  icon={Notification01Icon}
                  color={palette.primary.main}
                  size={24}
                  style={{
                    zIndex: 2,
                  }}
                />
              </Box>
              <Stack alignItems={"center"}>
                <Typography variant="h1">Pedido Entrante</Typography>
                {order?.deliveryLocation?.distance?.text && order?.deliveryLocation?.duration?.text ? (
                  <Typography variant="body2">
                    {order.deliveryLocation.distance.text} / {order.deliveryLocation.duration.text}
                  </Typography>
                ) : null}
                <Typography
                  textTransform={"uppercase"}
                  color="white"
                  sx={{
                    bgcolor: (order?.flow as unknown as MENU_FLOWS) === MENU_FLOWS.PICK_UP ? "#4DB6AC" : "#F57F17",
                    borderRadius: 1,
                    px: 1,
                  }}
                >
                  {order?.flow ? MENU_FLOW_LABELS[order?.flow as unknown as MENU_FLOWS] : "n/a"}
                </Typography>
              </Stack>
            </Stack>
            <Grid container spacing={3} direction={"row"}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ flex: 1, height: "100%", border: `1px solid ${palette.grey[100]}` }}>
                  <Stack p={2} spacing={2} height="100%">
                    <Typography variant="h4">Pedido</Typography>
                    <Stack spacing={1} flex={1} height={"100%"}>
                      {order?.items?.map((i) => (
                        <Stack key={`order-item-${i?.id}`} direction={"row"} spacing={2}>
                          <Typography variant="body2">{i.quantity}</Typography>
                          <Stack>
                            <Typography variant="body2" flex={1}>
                              {i.title}
                            </Typography>
                            <Typography variant="body1">{getOrderDetails(i)}</Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                    <Divider />
                    <Stack direction={"row"} spacing={2} justifyContent={"space-between"}>
                      <Typography variant="h4">Total</Typography>
                      <Typography variant="h4">
                        {order?.items ? (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Chip label="nuevo" size="small" sx={{ backgroundColor: purple[500], color: "white" }} />
                            <Price currency="NIO">{order?.deliveryFee ? getOrderTotal(order.items as OrderItem[], !!organization?.settings?.includeTax, Number(order?.deliveryFee)) : getOrderTotal(order.items as OrderItem[], !!organization?.settings?.includeTax, Number(service.fee))}</Price>
                          </Stack>
                        ) : (
                          "n/a"
                        )}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ flex: 1, height: "100%", border: `1px solid ${palette.grey[100]}` }}>
                  <Stack p={2}>
                    <Typography variant="h4">Cliente</Typography>
                    <Stack spacing={1}>
                      {order?.client?.name ? <DataCell vertical label="Nombre" value={`${order?.client.name} ${order?.client?.lastname ?? ""}`} /> : null}
                      {order?.client?.phone ? (
                        <Stack direction={"row"} spacing={1} alignContent={"center"} justifyContent={"space-between"}>
                          <DataCell vertical label="Teléfono" value={formatPhone(order?.client.phone)} />
                          <Tooltip title="Enviar mensaje" placement="top" arrow>
                            <HugeiconsIcon icon={WhatsappIcon} color={palette.primary.main} size={22} onClick={() => window.open(`https://wa.me/505${order?.client.phone}`)} />
                          </Tooltip>
                        </Stack>
                      ) : null}
                      {/* {order?.deliveryLocation?.neighborhood ? <DataCell vertical label="Barrio" value={getNeighborhoodName(order?.deliveryLocation?.neighborhood)} /> : null} */}
                      {order?.deliveryLocation?.address ? <DataCell vertical label="Dirección" value={order?.deliveryLocation?.address} /> : null}
                    </Stack>
                  </Stack>
                </Card>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Button onClick={() => setIsRejecting(true)} fullWidth color="error" sx={{ borderRadius: 1, background: "linear-gradient(180deg, #EF7674 0%, #C63E3C 100%)" }}>
                  Rechazar
                </Button>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Button onClick={onAcceptOrder} fullWidth color="primary" sx={{ borderRadius: 1, background: "linear-gradient(180deg, #56C65B 0%, #4CAF50 100%)" }}>
                  Aceptar
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Dialog>
  );
};

export default IncomingOrderDialog;
